import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../components/base/BasicDialog";
import { SnackbarError } from "../../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import { useSendOfferMutation, useUpdateOfferMutation } from "../../../services/offersService";
import { normalizePercentageForSubmission } from "../../../utils";
import { DialogTabs, DialogTabsEnum } from "./DialogTabs";
import { editOfferFormDataSchema } from "./EditOfferFormData";
import { SubmitButton } from "./SubmitButton";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import ConfirmationDialog from "../../../components/dialog/ConfirmationDialog/confirmationDialog";
import { isDictsAreEqual } from "../../../helpers/methods";

export const OfferEditDialog = (props) => {
    const { initialOffer, initialTab } = props;
    const { t } = useTranslation();

    const [offer, setOffer] = useState(initialOffer);
    const [queuedSendRequest, setQueuedSendRequest] = useState(null);
    const [saveOfferBeforeExit, setSaveOfferBeforeExit] = useState(null);
    // Add selectedTab state management
    const [selectedTab, setSelectedTab] = useState(initialTab ?? DialogTabsEnum.CUSTOMER_SELECTION);
    const bodyEditorRef = useRef()

    const onClose = () => {
        props.onClose();
        props.onCloseAlways?.();
    };

    const initialForm = {
        customerIds: offer.customers.map((customer) => customer.customer.id),
        discount: Object.fromEntries(
            offer.products.map((op) => [op.product.id, Number(op.discount_percent)]),
        ),
        draftId: offer.draft_id || undefined,
        expireDate: offer.expire_date ? new Date(offer.expire_date) : undefined,
        email_subject: offer.email_subject,
        email_body: offer.email_body,
    }

    const form = useForm({
        resolver: zodResolver(editOfferFormDataSchema),
        defaultValues: initialForm,
    });

    const isOfferChanged = ()=> {
        const currentForm = {
            customerIds: form.watch('customerIds'),
            discount: form.watch('discount'),
            draftId: form.watch('draftId'),
            expireDate: form.watch('expireDate'),
            email_subject: form.watch('email_subject'),
            email_body: bodyEditorRef.current?.getHtml() || ""
        }
        return !isDictsAreEqual(initialForm, currentForm)
    }

    const { isPending: isPendingSendOffer, mutate: sendOffer } = useSendOfferMutation({
        onSuccess: (response) => {
            toast.custom(
                (curToast) => (
                    <SnackbarSuccess message={t("offers.OfferEditDialog.offerSavedAndSent")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            console.error("sendOffer", error);
            toast.custom(
                (curToast) => <SnackbarError message={t("offers.OfferEditDialog.sendFailed")} />,
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const { isPending: isPendingUpdateOffer, mutate: updateOffer } = useUpdateOfferMutation({
        onSuccess: (offer) => {
            toast.custom(
                (curToast) => (
                    <SnackbarSuccess message={t("offers.OfferEditDialog.offerUpdated")} />
                ),
                {
                    position: "bottom-right",
                },
            );
            setOffer(offer);

            if (queuedSendRequest) {
                sendOffer({ offerId: offer.id, data: queuedSendRequest });
                setQueuedSendRequest(null);
            }
            
            if (saveOfferBeforeExit === true) {
                onClose()
            }

            props.onSuccess?.();
        },
        onError: (error) => {
            console.error("updateOffer", error);
            setQueuedSendRequest(null);
            toast.custom(
                (curToast) => (
                    <SnackbarError message={t("offers.OfferEditDialog.offerUpdateFailed")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const {
        openDialog: openConfirmationExitDialog,
        closeDialog: closeConfrimationExitDialog,
    } = useDialogBetter()

    const onSubmit = (data) => {
        const requestBody = {
            customer_ids: data.customerIds,
            products: Object.entries(data.discount).map(([productId, discountPercent]) => ({
                product_id: productId,
                discount_percent: normalizePercentageForSubmission(discountPercent),
            })),
            draft_id: data.draftId || undefined,
            expire_date: data.expireDate?.toISOString().split("T")[0],
            email_body: bodyEditorRef.current?.getHtml() || "",
            email_subject: data.email_subject || "",
        };
        updateOffer({ offerId: offer.id, data: requestBody });
    };

    const getRequestDataForGenerateOffer = () => {
        const data = form.getValues()
        const requestBody = {
            customer_ids: data.customerIds,
            products: Object.entries(data.discount).map(([productId, discountPercent]) => ({
                product_id: productId,
                discount_percent: normalizePercentageForSubmission(discountPercent),
            })),
        }; 
        return requestBody
    }

    const onSaveAndSend = (sendRequest) => {
        setQueuedSendRequest(sendRequest);
        onSubmit(form.getValues());
    };

    const handleOpenConfirmationDialog =()=> openConfirmationExitDialog({
            Component: ConfirmationDialog,
            componentProps: {
            title: t("offers.OfferEditDialog.alert"),
            onYes: () => {
                closeConfrimationExitDialog()
                setSaveOfferBeforeExit(true)
                onSubmit(form.getValues())
            },
            onNo: () => {
                closeConfrimationExitDialog()
                onClose()
            },
            onCancel: () => {
                closeConfrimationExitDialog()
            },
            content: `${t("offers.OfferEditDialog.confirmExit")}`, 
            showCancelButton: true,
            showCloseButton: true,
            },
        })

    return (
        <BasicDialog
            open={props.open}
            onClose={()=>isOfferChanged() ? handleOpenConfirmationDialog() : onClose()}
            alwaysFullScreen={true}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("offers.OfferEditDialog.title")}
            showDialogActions
        >
            <FormProvider {...form}>
                <form>
                    <DialogTabs
                        control={form.control}
                        fields={{
                            customerIds: "customerIds",
                            discount: "discount",
                        }}
                        offer={offer}
                        onSaveAndSend={onSaveAndSend}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        isRequestSend={isPendingSendOffer || isPendingUpdateOffer}
                        getRequestDataForGenerateOffer={getRequestDataForGenerateOffer}
                        bodyEditorRef={bodyEditorRef}
                    />
                    <SubmitButton
                        fields={{
                            customerIds: "customerIds",
                            discount: "discount",
                        }}
                        isLoading={isPendingSendOffer || isPendingUpdateOffer}
                        onSubmit={onSubmit}
                    />
                </form>
            </FormProvider>
        </BasicDialog>
    );
};
